/**
 * Tiny client-side HTML sanitizer for the Notes rich-text composer.
 *
 * Goals:
 * - Preserve common formatting on paste: <b>, <strong>, <i>, <em>, <u>, <a>,
 *   <ul>, <ol>, <li>, <p>, <br>, <code>, <pre>, <blockquote>.
 * - Strip everything else (scripts, styles, MS-Word junk, inline event handlers).
 * - Strip all attributes except `href` on anchors, with http(s)/mailto-only.
 *
 * We never trust this on the server — the server stores `content_html` as
 * an opaque blob and re-sanitizes on render. Server-side sanitization is
 * out of scope for V1 since notes are only read inside the SPA we control.
 */

const ALLOWED_TAGS = new Set([
  'b', 'strong', 'i', 'em', 'u', 's',
  'a', 'br', 'p', 'div', 'span',
  'ul', 'ol', 'li',
  'code', 'pre', 'blockquote',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
]);

const ALLOWED_HREF_RX = /^(https?:|mailto:)/i;

const _walk = (node, parent) => {
  // Element nodes
  if (node.nodeType === 1) {
    const tag = node.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      // Replace with its inner content (preserves text)
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
      return;
    }
    // Strip all attributes except href on anchors
    for (const attr of Array.from(node.attributes)) {
      const name = attr.name.toLowerCase();
      if (tag === 'a' && name === 'href' && ALLOWED_HREF_RX.test(attr.value)) {
        // Keep, but force safe target/rel
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
        continue;
      }
      node.removeAttribute(attr.name);
    }
    // Recurse — clone the children list because we may mutate it
    for (const c of Array.from(node.childNodes)) _walk(c, node);
    return;
  }
  // Text / Comment / etc.
  if (node.nodeType === 8) parent.removeChild(node); // strip comments
};

export const sanitizeHtml = (html) => {
  if (!html) return '';
  // Strip MS-Office wrappers and Google Docs metadata up-front
  let cleaned = String(html)
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<o:p\b[^>]*>[\s\S]*?<\/o:p>/gi, '')
    .replace(/\sstyle\s*=\s*"[^"]*"/gi, '')
    .replace(/\sstyle\s*=\s*'[^']*'/gi, '')
    .replace(/<(\/?)(script|style|meta|link|object|embed|iframe|form|input|button|svg|img)\b[^>]*>/gi, '');
  const wrap = document.createElement('div');
  wrap.innerHTML = cleaned;
  for (const c of Array.from(wrap.childNodes)) _walk(c, wrap);
  return wrap.innerHTML;
};

/** Strip HTML → plain text for the `content` field (used by search + emails). */
export const htmlToText = (html) => {
  if (!html) return '';
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  // Insert newlines for block-level boundaries
  wrap.querySelectorAll('p, div, li, br, h1, h2, h3, h4, h5, h6, blockquote').forEach((el) => {
    el.insertAdjacentText('beforeend', '\n');
  });
  return (wrap.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};
